import moment from 'moment-timezone';
import {
  FacilityBookingSummary,
  FacilityBookingsDay,
  FacilityDay,
} from '../../../model/bookings/FacilityBookingsDay';
import { FacilityScheduleForDay } from '../../../model/bookings/FacilityScheduleForDay';
import { Slot } from '../../../model/bookings/Slot';
import { Booking } from '../../../model/bookings/Booking';

/**
 * Converts the lean /bookings response (facility schedule + bookings) into the
 * per-interval Slot/Booking shape the legacy BookingScheduleTable / BookingCell /
 * MakeBooking flow consumes. The backend no longer sends per-interval rows —
 * we derive them here from each facility's schedule + intervalMins + bookings.
 *
 * Keeps the wire payload small while leaving the existing rendering pipeline
 * (click handlers, mobile swipe, modal, masking) untouched.
 */

const parseHHmmOn = (hhmm: string, dateYmd: string, timezone: string): Date => {
  // Schedule strings arrive as "HH:mm" wall-clock time in the *facility's own
  // timezone* (e.g. "06:45" means 06:45 BST for a London club in summer, 06:45
  // GMT in winter). Anchor them at the matching real UTC instant so the grid's
  // cell boundaries line up with the bookings — which carry true UTC instants —
  // and the display layer (formatInTimezone / moment.tz) renders the original
  // wall-clock time rather than shifting it by the UTC offset.
  return moment.tz(`${dateYmd} ${hhmm}`, 'YYYY-MM-DD HH:mm', timezone).toDate();
};

const toLegacyBooking = (b: FacilityBookingSummary, facilityName: string): Booking => ({
  bookingId: b.id,
  bookingDateTimeUtc: new Date(b.start),
  bookingEndTimeUtc: new Date(b.end),
  facilityName,
  bookingStatus: (b.status as unknown as string) ?? 'Booked',
  paymentTransactionStatus: b.paymentStatus,
  memberName: b.memberName,
  membershipId: b.membershipId,
  playingMembers: b.playingMembers,
  transactionAmount: b.transactionAmount ?? null,
  metadata: buildBookingMetadata(b),
});

const buildBookingMetadata = (b: FacilityBookingSummary): Record<string, unknown> => {
  const meta: Record<string, unknown> = {};
  if (b.title != null) meta.BookingTitle = b.title;
  if (b.price != null) meta.Price = String(b.price);
  if (b.notes != null) meta.BookingNotes = b.notes;
  meta.NonMembers = String(b.nonMembers);
  return meta;
};

const adaptFacility = (
  facility: FacilityDay,
  date: Date,
  timezone: string,
  dateYmd: string
): FacilityScheduleForDay | null => {
  if (!facility.schedule) return null;

  const intervalMs = facility.intervalMins * 60_000;
  const startMs = parseHHmmOn(facility.schedule.start, dateYmd, timezone).getTime();
  const endMs = parseHHmmOn(facility.schedule.end, dateYmd, timezone).getTime();

  const sortedBookings = [...facility.bookings]
    .filter(b => (b.status as unknown as string) !== 'Cancelled')
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Resolve which booking owns each interval using the SAME claiming rule as
  // BookingScheduleTable (the table re-derives the grid from bookings[], so the
  // adapter's per-slot status must agree with it). Bookings are processed
  // earliest-first; the first booking to claim a start slot wins, and a later
  // booking whose start slot is already taken is dropped entirely — its slots
  // are NOT marked booked. A naive "any overlapping booking" lookup would bleed
  // a spurious 'Booked' onto a cell the table renders as available.
  const claimedBy = new Map<number, FacilityBookingSummary>();
  for (const b of sortedBookings) {
    const bStart = new Date(b.start).getTime();
    const bEnd = new Date(b.end).getTime();
    if (claimedBy.has(bStart)) continue; // start slot already owned → drop booking
    for (let t = bStart; t < bEnd; t += intervalMs) {
      if (claimedBy.has(t)) break; // stop at the first slot owned by an earlier booking
      claimedBy.set(t, b);
    }
  }

  // Slots beyond the member advance-booking window are surfaced as Unavailable
  // so members can't click them. BookingCell's admin gate lets admins bypass
  // (if (!isUnavailable || isAdmin) → click proceeds), preserving the old
  // server-side MaskForNonAdmin behaviour without special-casing the adapter
  // to know about user roles.
  const advanceWindowCutoffMs = facility.advanceBookingWindowDays != null
    ? Date.now() + facility.advanceBookingWindowDays * 24 * 60 * 60 * 1000
    : Number.POSITIVE_INFINITY;

  // Stop once the shortest possible booking (start + one intervalMins) would run
  // past the schedule end. The loop steps by intervalMs, so the last slot we emit
  // is the latest start whose minimum-length booking still finishes by endMs. A
  // plain `t < endMs` would emit a final slot that, if booked, overruns the
  // schedule — e.g. a 45-min squash facility closing at 22:00 must not offer a
  // 21:45 slot that would end at 22:30.
  const slots: Slot[] = [];
  for (let t = startMs; t + intervalMs <= endMs; t += intervalMs) {
    const covering = claimedBy.get(t) ?? null;
    const slotStart = new Date(t);
    const slotEnd = new Date(t + intervalMs);
    const beyondAdvanceWindow = t >= advanceWindowCutoffMs;

    if (covering) {
      slots.push({
        startTime: slotStart,
        endTime: slotEnd,
        // Carry the booking's real status (e.g. 'Unavailable' for blocks), which
        // drives BookingCell's grey-out and disabled-click logic. Hard-coding
        // 'Booked' would make a block look like an ordinary, clickable booking.
        bookingStatus: covering.status as unknown as string,
        facilityName: facility.name,
        facilityType: facility.type,
        timezone,
        // Preserve the covering booking_slot id so the POS check-in / payment
        // flow can reference it (FK into dbo.booking_slot). Without it the slot
        // sent back to the API carries no BookingId and the update fails the FK.
        bookingId: covering.id,
        metadata: buildBookingMetadata(covering),
      });
    } else {
      slots.push({
        startTime: slotStart,
        endTime: slotEnd,
        bookingStatus: beyondAdvanceWindow ? 'Unavailable' : 'Available',
        facilityName: facility.name,
        facilityType: facility.type,
        timezone,
        metadata: {},
      });
    }
  }

  const bookings: Booking[] = sortedBookings.map(b => toLegacyBooking(b, facility.name));

  const metadata: Record<string, string> = {};
  if (facility.price != null) metadata.Price = String(facility.price);
  if (facility.bookingType != null) metadata.FacilityBookingType = facility.bookingType;
  if (facility.advanceBookingWindowDays != null) {
    metadata.MemberAdvanceBookingWindow = String(facility.advanceBookingWindowDays);
  }
  metadata.CheckinEnabled = String(facility.checkinEnabled);
  metadata.PaymentEnabled = String(facility.paymentEnabled);

  return {
    facilityName: facility.name,
    facilityType: facility.type,
    bookingIntervalMins: facility.intervalMins,
    timezone,
    date,
    slots,
    bookings,
    metadata,
    // A facility the server omitted `isActive` for (an older API, or a fixture
    // predating FORT-401) is treated as active — the grid must not grey out every
    // court because a field is missing.
    isActive: facility.isActive !== false,
  };
};

export const adaptBookingsToSchedules = (
  day: FacilityBookingsDay | undefined,
  selectedDate: Date
): FacilityScheduleForDay[] => {
  if (!day) return [];
  // The day's calendar date is authoritative for anchoring schedule wall-clock
  // times in the club timezone. NB: `day.date` is typed as string, but the API
  // client's DateReviver converts any field named "date" into a Date object at
  // runtime — so it can arrive as either. Normalise to a YYYY-MM-DD calendar day
  // either way (UTC components for a revived Date, which was parsed from a
  // date-only string at UTC midnight), falling back to the selected date.
  const rawDate = day.date as unknown;
  const dateYmd =
    rawDate instanceof Date
      ? moment.utc(rawDate).format('YYYY-MM-DD')
      : (typeof rawDate === 'string' ? rawDate.slice(0, 10) : '') ||
        moment(selectedDate).format('YYYY-MM-DD');
  return day.facilities
    .map(f => adaptFacility(f, selectedDate, day.timezone, dateYmd))
    .filter((s): s is FacilityScheduleForDay => s !== null);
};
